import type { ColumnSizingState, Updater } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";

import {
  LAYOUT_VERSION,
  loadTableState,
  savedUserWidths,
  saveTableState,
} from "./persistence";
import type {
  ColumnPinning,
  ColumnVisibilityModel,
  Density,
  FilterModel,
  SortModel,
} from "./types";

interface TableLayoutOptions {
  persistKey?: string;
  initialColumnVisibility?: ColumnVisibilityModel;
  columnVisibilityOverrides?: ColumnVisibilityModel;
  columnVisibilityModel?: ColumnVisibilityModel;
  onColumnVisibilityModelChange?: (model: ColumnVisibilityModel) => void;
  columnWidths?: ColumnSizingState;
  onColumnWidthsChange?: (widths: ColumnSizingState) => void;
  initialPinning?: Partial<ColumnPinning>;
  filterModel: FilterModel;
  sortModel: SortModel;
}

function resolve<T>(updater: Updater<T>, current: T): T {
  return typeof updater === "function"
    ? (updater as (old: T) => T)(current)
    : updater;
}

export default function useTableLayout({
  persistKey,
  initialColumnVisibility,
  columnVisibilityOverrides,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  columnWidths,
  onColumnWidthsChange,
  initialPinning,
  filterModel,
  sortModel,
}: TableLayoutOptions) {
  const saved = useMemo(
    () => (persistKey ? loadTableState(persistKey) : undefined),
    [persistKey],
  );
  const savedColumns =
    saved?.layoutVersion === LAYOUT_VERSION ? saved.columns : undefined;
  const defaultVisibility: ColumnVisibilityModel = {
    ...initialColumnVisibility,
    ...columnVisibilityOverrides,
  };
  const defaultPinning: ColumnPinning = {
    start: initialPinning?.start ?? [],
    end: initialPinning?.end ?? [],
  };

  // Overrides beat saved visibility and reapply whenever they change (e.g. per tab).
  const [ownVisibility, setOwnVisibility] = useState<ColumnVisibilityModel>(
    () => ({
      ...initialColumnVisibility,
      ...savedColumns?.columnVisibilityModel,
      ...columnVisibilityOverrides,
    }),
  );
  const overridesKey = JSON.stringify(columnVisibilityOverrides ?? {});
  useEffect(() => {
    setOwnVisibility((current) => ({
      ...current,
      ...JSON.parse(overridesKey),
    }));
  }, [overridesKey]);
  // A parent-held model wins, so two tables can share one.
  const columnVisibility = columnVisibilityModel ?? ownVisibility;
  const setColumnVisibility = (updater: Updater<ColumnVisibilityModel>) => {
    const next = resolve(updater, columnVisibility);
    setOwnVisibility(next);
    onColumnVisibilityModelChange?.(next);
  };
  const [columnOrder, setColumnOrder] = useState<string[]>(
    () => savedColumns?.orderedFields ?? [],
  );
  const [ownSizing, setOwnSizing] = useState<ColumnSizingState>(() =>
    savedUserWidths(saved),
  );
  const columnSizing = columnWidths ?? ownSizing;
  const setColumnSizing = (updater: Updater<ColumnSizingState>) => {
    const next = resolve(updater, columnSizing);
    setOwnSizing(next);
    onColumnWidthsChange?.(next);
  };
  const [columnPinning, setColumnPinning] = useState<ColumnPinning>(() =>
    saved?.layoutVersion === LAYOUT_VERSION && saved.pinning
      ? saved.pinning
      : defaultPinning,
  );
  const [density, setDensity] = useState<Density>(
    () => saved?.density ?? "standard",
  );

  // Only the user's changes are saved, so untouched columns follow the current defaults.
  const changedVisibility = Object.fromEntries(
    Object.entries(columnVisibility).filter(
      ([field, visible]) => visible !== (defaultVisibility[field] ?? true),
    ),
  );
  const changedVisibilityKey = JSON.stringify(changedVisibility);

  // biome-ignore lint/correctness/useExhaustiveDependencies: changedVisibilityKey stands in for changedVisibility
  useEffect(() => {
    if (!persistKey) return;
    saveTableState(persistKey, {
      layoutVersion: LAYOUT_VERSION,
      columns: {
        columnVisibilityModel: changedVisibility,
        orderedFields: columnOrder,
        dimensions: Object.fromEntries(
          Object.entries(columnSizing).map(([field, width]) => [
            field,
            { width },
          ]),
        ),
      },
      pinning: columnPinning,
      filter: { filterModel },
      sorting: { sortModel },
      density,
    });
  }, [
    persistKey,
    changedVisibilityKey,
    columnOrder,
    columnSizing,
    columnPinning,
    filterModel,
    sortModel,
    density,
  ]);

  const resetLayout = () => {
    setColumnVisibility(defaultVisibility);
    setColumnOrder([]);
    setColumnSizing({});
    setColumnPinning(defaultPinning);
    setDensity("standard");
  };

  return {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    columnSizing,
    setColumnSizing,
    columnPinning,
    setColumnPinning,
    density,
    setDensity,
    resetLayout,
  };
}
